import React, { useEffect, useState } from "react";
import { Modal, Form, InputNumber, Select, message, Card, Divider } from "antd";
import ProductServ from "../../service/ProductServ";
import ProductInventoryThresholdServ from "../../service/ProductInventoryThresholdServ";
import GlobalButton from "../../util/GlobalButton";
import { getImageUrl } from "../../component/SafeImage";

const { Option } = Select;

const AddProductThreshold = ({ open, onClose, thresholdDetails, isEditing, refreshData }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Fetch products for the dropdown
  useEffect(() => {
    if (open) {
      const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
          const response = await ProductServ.index({ limit: "all" });
          setProducts(response?.data || []);
        } catch (error) {
          console.error(error);
          message.error("Failed to load products");
        } finally {
          setLoadingProducts(false);
        }
      };
      fetchProducts();
    }
  }, [open]);

  // Set form values if editing
  useEffect(() => {
    if (open && isEditing && thresholdDetails) {
      const pId = thresholdDetails.productId?._id || thresholdDetails.productId;
      form.setFieldsValue({
        productId: pId,
        minStock: thresholdDetails.minStock,
        maxStock: thresholdDetails.maxStock
      });
      // Set selected product for the info card
      if (thresholdDetails.product) {
          setSelectedProduct(thresholdDetails.product);
      } else if (pId) {
          // Fetch if not available
          ProductServ.single(pId).then(res => setSelectedProduct(res.data || res));
      }
    } else if (open) {
      form.resetFields();
      setSelectedProduct(null);
    }
  }, [open, thresholdDetails, isEditing, form]);

  const onProductChange = (productId) => {
    const prod = products.find(p => (p._id || p.id) === productId);
    setSelectedProduct(prod);
  };

  const onFinish = async (values) => {
    if (values.minStock >= values.maxStock) {
        return message.error("Min Stock must be less than Max Stock");
    }

    try {
      setSubmitting(true);
      if (isEditing) {
        await ProductInventoryThresholdServ.update(thresholdDetails._id, values);
        message.success("Threshold updated successfully!");
      } else {
        await ProductInventoryThresholdServ.create(values);
        message.success("Threshold added successfully!");
      }
      onClose(true);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || err.message || "Error saving threshold");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEditing ? "Edit Inventory Threshold" : "Add Inventory Threshold"}
      open={open}
      onCancel={() => onClose(false)}
      width={600}
      footer={[
        <div className="form-footer-btn pcu-text-right" key="footer">
          <GlobalButton
            action="cancel"
            onClick={() => onClose(false)}
            disabled={submitting}
          >
            Cancel
          </GlobalButton>
          <GlobalButton
            action="submit"
            onClick={() => form.submit()}
            loading={submitting}
          >
            {isEditing ? "Update Threshold" : "Add Threshold"}
          </GlobalButton>
        </div>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ minStock: 10, maxStock: 100 }}
      >
        <Form.Item
          label="Select Product"
          name="productId"
          rules={[{ required: true, message: "Please select a product" }]}
        >
          <Select
            placeholder="Search and select product"
            showSearch
            onChange={onProductChange}
            optionLabelProp="label"
            optionFilterProp="title"
            disabled={isEditing}
            loading={loadingProducts}
            size="large"
          >
            {products.map((p) => (
              <Option
                key={p._id || p.id}
                value={p._id || p.id}
                title={p.name}
                label={p.name}
              >
                <div className="pcu-flex pcu-flex-center-y pcu-gap-12 pcu-padding-y-4">
                  <img
                    src={getImageUrl(p.imageUrl)}
                    alt={p.name}
                    className="pcu-w-40px pcu-h-40px pcu-radius-8 pcu-object-cover"
                  />
                  <div>
                    <div className="pcu-font-bold pcu-font-14">{p.name}</div>
                    <div className="pcu-font-12 pcu-color-muted">{p.category || 'Category N/A'}</div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedProduct && (
          <Card size="small" className="pcu-margin-bottom-20 pcu-bg-muted-light pcu-border-light pcu-radius-12">
            <div className="pcu-flex pcu-flex-center-y pcu-gap-16">
              <img
                src={getImageUrl(selectedProduct.imageUrl)}
                alt="Selected"
                className="pcu-w-60px pcu-h-60px pcu-radius-10 pcu-object-cover"
              />
              <div className="pcu-flex-1">
                <div className="pcu-font-bold pcu-font-15">{selectedProduct.name}</div>
                <div className="pcu-font-12 pcu-color-muted">
                    Current Stock: <span className="pcu-font-bold">{selectedProduct.stock || 0}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        <Divider />

        <div className="pcu-flex pcu-gap-20">
          <Form.Item
            label="Minimum Stock Threshold"
            name="minStock"
            className="pcu-flex-1"
            rules={[{ required: true, message: "Required" }]}
            tooltip="System will flag as LOW STOCK when inventory falls to or below this value."
          >
            <InputNumber min={0} className="pcu-w-100" size="large" />
          </Form.Item>

          <Form.Item
            label="Maximum Stock Threshold"
            name="maxStock"
            className="pcu-flex-1"
            rules={[{ required: true, message: "Required" }]}
            tooltip="System will flag as OVER STOCK when inventory reaches or exceeds this value."
          >
            <InputNumber min={1} className="pcu-w-100" size="large" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default AddProductThreshold;
